/**
 * @fileoverview WEB-1135 — visible title for event cards, Discover rows, and
 * event detail. Recases a shouting feed title with {@link toDisplayTitle}
 * and keeps the published string as the accessible name, matching the
 * city-home docket (WEB-1131). Display only: callers must not persist or
 * query the recased string.
 */

import type { ReactElement } from 'react';

import { toDisplayTitle } from './eventTitle';

export interface DisplayEventTitleProps {
  /** Title as published. */
  title: string;
  /**
   * Classes for the painted string (line-clamp, truncation). Applied only
   * to the visible text so the screen-reader copy is not clipped away.
   */
  className?: string;
}

/**
 * Paints a title-cased shouting title. The raw title stays the accessible
 * name: the cased string is `aria-hidden` and the published string is
 * `sr-only`. Mixed-case titles render unchanged.
 */
export function DisplayEventTitle({ title, className }: DisplayEventTitleProps): ReactElement {
  const displayTitle = toDisplayTitle(title);
  if (displayTitle === title) {
    // No wrapper when nothing changes, so existing title DOM stays a text node.
    if (className === undefined) return <>{title}</>;
    return <span className={className}>{title}</span>;
  }
  return (
    <>
      <span aria-hidden="true" className={className}>
        {displayTitle}
      </span>
      <span className="sr-only">{title}</span>
    </>
  );
}
